import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { PdfViewerModal } from "../components/PdfViewerModal";
import { Trash, Plus, FilePdf, ArrowLeft, CheckCircle, Circle, Printer } from "@phosphor-icons/react";

const AUDIT_STATUS = [["in_review", "In review"], ["submitted", "Submitted"], ["completed", "Completed"]];

export default function PayerAuditsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [audits, setAudits] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({});
  const [pdfView, setPdfView] = useState(null);

  const load = () => api.get("/payer-audits").then((r) => {
    setAudits(r.data.audits);
    setSelected((sel) => sel ? r.data.audits.find((a) => a.id === sel.id) || null : null);
  }).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  useEffect(() => {
    if (!selected?.analyzing) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [selected?.analyzing]); // eslint-disable-line

  if (!isAdmin) {
    return <div className="rounded-lg border border-stone-200 bg-white p-6 text-center text-sm text-stone-400" data-testid="payer-audits-denied">Admins only.</div>;
  }

  const createAudit = async () => {
    setBusy(true);
    try {
      await api.post("/payer-audits", { template: "keystone_ltss_pnm", ...newForm });
      toast.success("Audit created with the full Keystone question set");
      setShowNew(false);
      setNewForm({});
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const analyzeAll = async () => {
    try {
      await api.post(`/payer-audits/${selected.id}/analyze-all`);
      toast.success("AI is analyzing every question — this takes a few minutes; answers appear as they complete");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadCompleted = async () => {
    try {
      const { data } = await api.get(`/payer-audits/${selected.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      const aEl = document.createElement("a");
      aEl.href = url; aEl.download = `COMPLETED - ${selected.filename || "survey.xlsx"}`; aEl.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadSurvey = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/payer-audits/upload", fd, { timeout: 120000 });
      toast.success("Survey uploaded — open it and run “Analyze all with AI”");
      setShowNew(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const updateAudit = async (aid, patch) => {
    try {
      await api.put(`/payer-audits/${aid}`, patch);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateItem = async (aid, item, patch) => {
    setAudits((prev) => prev.map((a) => a.id === aid
      ? { ...a, items: a.items.map((i) => i.id === item.id ? { ...i, ...patch } : i) } : a));
    setSelected((sel) => sel && sel.id === aid
      ? { ...sel, items: sel.items.map((i) => i.id === item.id ? { ...i, ...patch } : i) } : sel);
    try {
      await api.put(`/payer-audits/${aid}/items/${item.id}`, patch);
    } catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const prefillAnswers = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/payer-audits/${selected.id}/prefill`, {}, { timeout: 300000 });
      toast.success(`Drafted ${data.filled} answers from your policies${data.reused ? ` + reused ${data.reused} of your approved past answers` : ""} — review each before marking ready`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const delAudit = async (a) => {
    if (!window.confirm(`Delete the ${a.payer} audit?`)) return;
    try {
      await api.delete(`/payer-audits/${a.id}`);
      toast.success("Deleted");
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (selected) {
    const a = selected;
    const ready = (a.items || []).filter((i) => i.ready).length;
    const sections = [];
    (a.items || []).forEach((i) => {
      if (!sections.includes(i.section)) sections.push(i.section);
    });
    return (
      <div className="max-w-5xl" data-testid="payer-audit-detail">
        <button onClick={() => setSelected(null)} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline" data-testid="payer-audit-back">
          <ArrowLeft size={15} /> All payer audits
        </button>
        <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="font-heading text-lg font-bold text-stone-900" data-testid="payer-audit-title">{a.name}</h1>
              <p className="text-xs text-stone-400">{a.payer} · due {a.date_due || "—"} · {ready}/{a.items?.length || 0} ready</p>
            </div>
            <div className="flex items-center gap-2">
              <select value={a.status} onChange={(e) => updateAudit(a.id, { status: e.target.value })}
                className="h-9 rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="payer-audit-status">
                {AUDIT_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              {a.template === "uploaded" ? (
                <>
                  <Button onClick={analyzeAll} disabled={!!a.analyzing} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="payer-audit-analyze-all">
                    {a.analyzing ? "AI analyzing…" : "Analyze all with AI"}
                  </Button>
                  <Button variant="outline" onClick={downloadCompleted} className="border-stone-300" data-testid="payer-audit-download-completed">
                    <FilePdf size={15} className="mr-1.5" /> Download completed workbook
                  </Button>
                </>
              ) : (
                <Button variant="outline" onClick={prefillAnswers} disabled={busy} className="border-stone-300" data-testid="payer-audit-prefill">
                  {busy ? "Drafting…" : "Draft answers from policies (AI)"}
                </Button>
              )}
              <Button variant="outline" onClick={() => setPdfView({ url: `${api.defaults.baseURL}/payer-audits/${a.id}/export`, title: `${a.payer} audit response` })}
                className="border-stone-300" data-testid="payer-audit-export">
                <FilePdf size={15} className="mr-1.5" /> Response packet + policies (PDF)
              </Button>
              <button onClick={() => delAudit(a)} className="text-stone-300 hover:text-[#C0392B]" data-testid="payer-audit-delete" title="Delete"><Trash size={16} /></button>
            </div>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-stone-100">
            <div className="h-full rounded-full bg-[#27AE60] transition-all" style={{ width: `${a.items?.length ? (ready / a.items.length) * 100 : 0}%` }} data-testid="payer-audit-progress" />
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {[["provider_contact", "Provider Contact Name/Title", "payer-field-provider"], ["account_executive", "Account Executive", "payer-field-ae"],
              ["date_requested", "Date Policies Requested", "payer-field-requested"], ["date_due", "Date Policies Due", "payer-field-due"],
              ["date_review_completed", "Date Review Completed", "payer-field-completed"], ["result", "Result of Review", "payer-field-result"],
              ["date_corrections_received", "Date Corrections Received", "payer-field-corrections"], ["date_cap_initiated", "Date CAP Initiated", "payer-field-cap"]].map(([key, label, tid]) => (
              <div key={key}>
                <label className="mb-1 block text-xs font-medium text-stone-500">{label}</label>
                <Input defaultValue={a[key] || ""} onBlur={(e) => { if (e.target.value !== (a[key] || "")) updateAudit(a.id, { [key]: e.target.value }); }}
                  className="h-8 border-stone-200 bg-white text-xs" data-testid={tid} />
              </div>
            ))}
          </div>
        </div>

        {a.items.some((i) => i.gap) && (
          <div className="mt-4 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-4 text-xs text-[#8a5d14]" data-testid="payer-gap-banner">
            <strong>{a.items.filter((i) => i.gap).length} question(s) have no policy coverage.</strong> They were left blank on purpose rather than filled with boilerplate — add the missing document under Knowledge, then re-analyze those items.
          </div>
        )}
        {a.items.some((i) => (i.policies_to_print || []).length > 0) && (
          <div className="mt-4 rounded-lg border border-primary/20 bg-primary/5 p-4" data-testid="payer-print-packet">
            <p className="text-xs font-bold uppercase tracking-wide text-primary"><Printer size={12} className="mr-1 inline" />Surveyor print packet — hand these over</p>
            <ul className="mt-2 space-y-1 text-xs text-stone-600">
              {[...new Map(a.items.flatMap((i) => (i.policies_to_print || [])).filter((p) => p.title).map((p) => [p.title, p])).values()].map((p, i) => (
                <li key={i} data-testid={`print-packet-${i}`}><strong>{p.title}</strong>{p.underline ? ` — underline: “${p.underline}”` : ""}</li>
              ))}
            </ul>
          </div>
        )}
        {sections.map((sec) => (
          <div key={sec} className="mt-4 rounded-lg border border-stone-200 bg-white shadow-sm" data-testid={`payer-section-${sec.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            <p className="border-b border-stone-100 px-5 py-3 font-heading text-sm font-semibold text-stone-900">{sec}</p>
            <div className="divide-y divide-stone-100">
              {(a.items || []).filter((i) => i.section === sec).map((item) => (
                <div key={item.id} className="px-5 py-3" data-testid={`payer-item-${item.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <p className="flex-1 text-sm text-stone-800">
                      {item.question}
                      {item.must_send_policy && (
                        <span className="ml-2 rounded-full border border-[#B7791F]/30 bg-[#B7791F]/10 px-2 py-0.5 text-[10px] font-bold text-[#7c4a03]">MUST SEND POLICY</span>
                      )}
                      {item.from_approved && (
                        <span className="ml-2 rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-2 py-0.5 text-[10px] font-bold text-[#1e8449]" data-testid={`payer-item-approved-${item.id}`}>FROM APPROVED ANSWER</span>
                      )}
                    </p>
                    <button onClick={() => { updateItem(a.id, item, { ready: !item.ready }); if (!item.ready && (item.answer || "").trim()) toast.success("Marked ready — saved to the audit knowledge base, future audits will reuse it"); }}
                      className={`mt-0.5 shrink-0 ${item.ready ? "text-[#27AE60]" : "text-stone-300 hover:text-stone-500"}`}
                      data-testid={`payer-item-ready-${item.id}`} title={item.ready ? "Ready" : "Mark ready"}>
                      {item.ready ? <CheckCircle size={20} weight="fill" /> : <Circle size={20} />}
                    </button>
                  </div>
                  <Textarea defaultValue={item.answer || ""} placeholder="Your answer — saved when you click away"
                    onBlur={(e) => { if (e.target.value !== (item.answer || "")) updateItem(a.id, item, { answer: e.target.value }); }}
                    className="mt-2 min-h-14 border-stone-200 bg-stone-50 text-sm" data-testid={`payer-item-answer-${item.id}`} />
                  {item.policy && !item.must_send_policy && (
                    <p className="mt-1 text-[11px] text-stone-400" data-testid={`payer-item-cites-${item.id}`}>Source: {item.policy}</p>
                  )}
                  {item.gap && (
                    <p className="mt-1 rounded-md border border-[#B7791F]/30 bg-[#B7791F]/5 px-2 py-1 text-[11px] font-medium text-[#8a5d14]" data-testid={`payer-item-gap-${item.id}`}>
                      NO POLICY COVERS THIS — {item.gap_note || "add one under Knowledge, then re-analyze"}. Want to add it? Tell me and we'll draft it together.
                    </p>
                  )}
                  {(item.policies_to_print || []).length > 0 && (
                    <p className="mt-1 text-[11px] text-stone-500" data-testid={`payer-item-print-${item.id}`}>
                      <Printer size={11} className="mr-1 inline" />Print for the surveyor: {(item.policies_to_print || []).map((p, i) => (
                        <span key={i} className="mr-2"><strong>{p.title}</strong>{p.underline ? <em> — underline: “{p.underline}”</em> : ""}</span>
                      ))}
                    </p>
                  )}
                  {item.must_send_policy && (
                    <Input defaultValue={item.policy || ""} placeholder="Policy you'll send (e.g. EVV Compliance Policy v2)"
                      onBlur={(e) => { if (e.target.value !== (item.policy || "")) updateItem(a.id, item, { policy: e.target.value }); }}
                      className="mt-2 h-8 border-stone-200 bg-white text-xs" data-testid={`payer-item-policy-${item.id}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="mt-4 rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
          <p className="font-heading text-sm font-semibold text-stone-900">Wrap-up</p>
          <div className="mt-2 grid gap-3">
            <Textarea defaultValue={a.summary_of_findings || ""} placeholder="Summary of Findings"
              onBlur={(e) => { if (e.target.value !== (a.summary_of_findings || "")) updateAudit(a.id, { summary_of_findings: e.target.value }); }}
              className="border-stone-200 bg-stone-50 text-sm" data-testid="payer-field-summary" />
            <Textarea defaultValue={a.next_steps || ""} placeholder="Next Steps"
              onBlur={(e) => { if (e.target.value !== (a.next_steps || "")) updateAudit(a.id, { next_steps: e.target.value }); }}
              className="border-stone-200 bg-stone-50 text-sm" data-testid="payer-field-nextsteps" />
          </div>
        </div>
        <PdfViewerModal url={pdfView?.url} title={pdfView?.title || ""} onClose={() => setPdfView(null)} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl" data-testid="payer-audits-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-stone-900" data-testid="payer-audits-title">Payer Audits</h1>
          <p className="mt-1 text-sm text-stone-500">MCO policy-review audits (e.g. Keystone First LTSS PNM). Answer the question set, mark the policies you'll send, export the response packet.</p>
        </div>
        <Button onClick={() => setShowNew((v) => !v)} variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="payer-audit-new-button">
          <Plus size={15} className="mr-1.5" /> {showNew ? "Cancel" : "New payer audit"}
        </Button>
      </div>

      {showNew && (
        <div className="mt-4 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="payer-audit-new-form">
          <p className="text-sm font-semibold text-stone-800">Keystone First — LTSS PNM Provider Audit Tool</p>
          <p className="mt-0.5 text-xs text-stone-400">Creates the full 45-question set: agency background, policies/training, complaints, EVV, missed shifts, critical incidents, starting services within 7 days.</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Provider Contact Name/Title</label>
              <Input value={newForm.provider_contact || ""} onChange={(e) => setNewForm((f) => ({ ...f, provider_contact: e.target.value }))} className="h-9 border-stone-300 bg-white text-sm" data-testid="payer-new-contact" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Account Executive</label>
              <Input value={newForm.account_executive || ""} onChange={(e) => setNewForm((f) => ({ ...f, account_executive: e.target.value }))} className="h-9 border-stone-300 bg-white text-sm" data-testid="payer-new-ae" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Date Policies Requested</label>
              <Input type="date" value={newForm.date_requested || ""} onChange={(e) => setNewForm((f) => ({ ...f, date_requested: e.target.value }))} className="h-9 border-stone-300 bg-white text-sm" data-testid="payer-new-requested" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Date Policies Due</label>
              <Input type="date" value={newForm.date_due || ""} onChange={(e) => setNewForm((f) => ({ ...f, date_due: e.target.value }))} className="h-9 border-stone-300 bg-white text-sm" data-testid="payer-new-due" />
            </div>
          </div>
          <Button onClick={createAudit} disabled={busy} className="mt-4 bg-primary text-primary-foreground hover:bg-stone-900" data-testid="payer-audit-create">
            {busy ? "Creating…" : "Create audit (45 questions preloaded)"}
          </Button>
          <div className="mt-4 border-t border-stone-100 pt-4">
            <p className="text-sm font-semibold text-stone-800">…or upload the payer's actual survey file</p>
            <p className="mt-0.5 text-xs text-stone-400">The .xlsx they sent you (e.g. the Keystone audit workbook). The AI reads every question, answers from your policies + approved past answers, and fills the file back in for download.</p>
            <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-full border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white" data-testid="payer-audit-upload-label">
              <Plus size={14} /> {busy ? "Uploading…" : "Upload survey xlsx"}
              <input type="file" accept=".xlsx" className="hidden" data-testid="payer-audit-upload-input"
                onChange={(e) => { uploadSurvey(e.target.files?.[0]); e.target.value = ""; }} disabled={busy} />
            </label>
          </div>
        </div>
      )}

      {audits === null ? (
        <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
      ) : audits.length === 0 ? (
        <p className="mt-4 rounded-lg border border-stone-200 bg-white p-6 text-center text-sm text-stone-400" data-testid="payer-audits-empty">No payer audits yet — start one above when a plan sends a review notice.</p>
      ) : (
        <div className="mt-4 space-y-3" data-testid="payer-audits-list">
          {audits.map((a) => {
            const pct = a.items_total ? Math.round((a.items_ready / a.items_total) * 100) : 0;
            return (
              <button key={a.id} onClick={() => setSelected(a)}
                className="w-full rounded-lg border border-stone-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-primary/40"
                data-testid={`payer-audit-row-${a.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-medium text-stone-900">{a.name}</p>
                    <p className="mt-0.5 text-xs text-stone-400">{a.payer} · due {a.date_due || "—"} · {AUDIT_STATUS.find(([v]) => v === a.status)?.[1] || a.status}</p>
                  </div>
                  <span className="text-xs text-stone-500" data-testid={`payer-audit-row-progress-${a.id}`}>{a.items_ready}/{a.items_total} ready ({pct}%)</span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-stone-100">
                  <div className="h-full rounded-full bg-[#27AE60]" style={{ width: `${pct}%` }} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
